import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'

import { useGatewaysList } from './hooks'
import { path } from '../../../libs'
import { toPath } from '../../../features/router'
import {
  Button,
  Flex,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { syncPaymentsMethod } from './model'
import { ColumnsType } from 'antd/lib/table'
import { Gateway, GatewayAccess, GatewayCommission, GatewayOperation, GatewayWeight, WithdrawalGroup } from './types'
import { Guard } from '../../../features/session/guard'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import { useUnit } from 'effector-react'
import { $countriesList, $countryNameByCode, dictionariesMounted } from '../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../libs/formatCountries'

const { Text } = Typography

/** Yes / No toggle — click active value again to reset */
function BoolFilter({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean | undefined
  onChange: (v: boolean | undefined) => void
}) {
  return (
    <Flex align="center" gap={6}>
      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{label}</Text>
      <Button
        type={value === true ? 'primary' : 'default'}
        onClick={() => onChange(value === true ? undefined : true)}
        style={{ fontSize: 12, padding: '0 12px' }}
      >
        Yes
      </Button>
      <Button
        type={value === false ? 'primary' : 'default'}
        danger={value === false}
        onClick={() => onChange(value === false ? undefined : false)}
        style={{ fontSize: 12, padding: '0 12px' }}
      >
        No
      </Button>
    </Flex>
  )
}

// ── Activity filter options ─────────────────────────────
const ACTIVITY_OPTIONS = [
  { label: 'Deposit enabled', value: 'deposit:enabled' },
  { label: 'Deposit disabled', value: 'deposit:disabled' },
  { label: 'Withdrawal enabled', value: 'withdrawal:enabled' },
  { label: 'Withdrawal disabled', value: 'withdrawal:disabled' },
]

export const GatewayList = () => {
  const { gateways, loading, onChangeGatewaysList, totalCountOfGateways } =
    useGatewaysList()

  const countriesList = useUnit($countriesList)
  const countryNameByCode = useUnit($countryNameByCode)

  useEffect(() => { dictionariesMounted() }, [])

  const { filters, setFilter, setFilters, resetFilters } = useUrlFilters({
    gatewayId: { type: 'string' as const },
    internalName: { type: 'string' as const },
    archived: { type: 'boolean' as const, default: false },
    page: { type: 'string' as const, default: '1', pagination: true },
    pageSize: { type: 'string' as const, default: '15', pagination: true },
    sortField: { type: 'string' as const, default: 'id', pagination: true },
    sortOrder: { type: 'string' as const, default: 'descend', pagination: true },
  })

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 15
  const orderBy = filters.sortField || 'id'
  const orderDirection = filters.sortOrder === 'ascend' ? '' : '-'

  // ── Client-side filters (activity, verify, country) ─────
  const [activityFilter, setActivityFilter] = useState<string[]>([])
  const [verifyRequired, setVerifyRequired] = useState<boolean | undefined>(undefined)
  const [paymentGroupFilter, setPaymentGroupFilter] = useState<string[]>([])
  const [countryFilter, setCountryFilter] = useState<string[]>([])

  // Local inputs — applied on Enter/blur
  const [inputId, setInputId] = useState(filters.gatewayId || '')
  const [inputName, setInputName] = useState(filters.internalName || '')

  const applyId = (value: string) => {
    const v = value.trim()
    setFilters({ gatewayId: v || undefined, page: '1' })
  }

  const applyName = (value: string) => {
    const v = value.trim()
    setFilters({ internalName: v || undefined, page: '1' })
  }

  // ── Server-side fetch ───────────────────────────────────
  useEffect(() => {
    const apiFilters: Record<string, any> = {}
    const archived = filters.archived ?? false
    if (archived !== undefined) apiFilters.isDisable = [String(archived)]

    // Server search: prefer gatewayId, fallback to internalName
    let searchText = ''
    let searchedColumn = ''
    if (filters.gatewayId) {
      searchText = filters.gatewayId
      searchedColumn = 'id'
    } else if (filters.internalName) {
      searchText = filters.internalName
      searchedColumn = 'internalName'
    }

    onChangeGatewaysList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      searchedColumn,
      filters: apiFilters,
    })
  }, [filters, pageSize, page, orderBy, orderDirection])

  // ── Client-side filtering (activity, verify) ────────────
  const filteredGateways = useMemo(() => {
    let result = gateways

    if (activityFilter.length > 0) {
      result = result.filter((g) => {
        return activityFilter.every((f) => {
          const [opType, status] = f.split(':')
          const op = g.gatewayOperations.find((o) => o.operationType === opType)
          if (!op) return status === 'disabled'
          return op.activity === status
        })
      })
    }

    if (verifyRequired !== undefined) {
      result = result.filter((g) => g.depositVerificationRequired === verifyRequired)
    }

    if (paymentGroupFilter.length > 0) {
      result = result.filter((g) => {
        const name = g.withdrawalGroup?.name || ''
        return paymentGroupFilter.includes(name)
      })
    }

    if (countryFilter.length > 0) {
      result = result.filter((g) =>
        g.gatewayAccess?.some((a) => {
          const countries = a.allowedCountries || []
          return countries.includes('*') || countryFilter.some((c) => countries.includes(c))
        }),
      )
    }

    return result
  }, [gateways, activityFilter, verifyRequired, paymentGroupFilter, countryFilter])

  // ── Country options from API dictionary ─────────────────
  const countryOptions = useMemo(
    () => transformCountriesToOptions(countriesList).map((c) => ({ label: c.text, value: c.value })),
    [countriesList],
  )

  // ── Payment group options from data ─────────────────────
  const paymentGroupOptions = useMemo(() => {
    const groups = new Set<string>()
    gateways.forEach((g) => {
      if (g.withdrawalGroup?.name) groups.add(g.withdrawalGroup.name)
    })
    return [...groups].sort().map((name) => ({ label: name, value: name }))
  }, [gateways])

  const hasActiveFilters =
    !!filters.gatewayId ||
    !!filters.internalName ||
    (filters.archived !== undefined && filters.archived !== false) ||
    activityFilter.length > 0 ||
    verifyRequired !== undefined ||
    paymentGroupFilter.length > 0 ||
    countryFilter.length > 0

  const resetAll = () => {
    resetFilters()
    setInputId('')
    setInputName('')
    setActivityFilter([])
    setVerifyRequired(undefined)
    setPaymentGroupFilter([])
    setCountryFilter([])
  }

  // ── Columns ─────────────────────────────────────────────
  const columns: ColumnsType<Gateway> = [
    {
      title: 'ID',
      dataIndex: 'gatewayId',
      sorter: (a, b) => parseInt(a.gatewayId) - parseInt(b.gatewayId),
      sortOrder:
        filters.sortField === 'id' ||
        filters.sortField === 'gatewayId' ||
        !filters.sortField
          ? ((filters.sortOrder || 'descend') as 'ascend' | 'descend')
          : undefined,
      width: 80,
      render: (v: string) => (
        <Text
          style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}
        >
          {v}
        </Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 180,
      render: (v: string, record: Gateway) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>{v}</Text>
          {record.internalName && record.internalName !== v && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>{record.internalName}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Activity',
      dataIndex: 'gatewayOperations',
      width: 150,
      render: (ops: GatewayOperation[]) => (
        <Flex gap={4} wrap="wrap">
          {ops.map((item) => (
            <Tag
              key={item.operationType}
              color={item.activity === 'enabled' ? 'success' : 'error'}
              style={{ fontSize: 10, margin: 0 }}
            >
              {item.operationType}
            </Tag>
          ))}
        </Flex>
      ),
    },
    {
      title: 'Supported',
      width: 130,
      render: (_: any, item: Gateway) => {
        if (!item.praxisGateway) return <Text type="secondary" style={{ fontSize: 11 }}>{'\u2014'}</Text>
        return (
          <Flex gap={4} wrap="wrap">
            <Tag color={item.praxisGateway.isDepositSupported ? 'success' : 'error'} style={{ fontSize: 10, margin: 0 }}>
              deposit
            </Tag>
            <Tag color={item.praxisGateway.isRefundSupported ? 'success' : 'error'} style={{ fontSize: 10, margin: 0 }}>
              refund
            </Tag>
            <Tag color={item.praxisGateway.isPayoutSupported ? 'success' : 'error'} style={{ fontSize: 10, margin: 0 }}>
              payout
            </Tag>
          </Flex>
        )
      },
    },
    {
      title: 'Verify',
      dataIndex: 'depositVerificationRequired',
      width: 70,
      align: 'center',
      render: (v: boolean) => (
        <Tag color={v ? 'success' : 'default'} style={{ fontSize: 10, margin: 0 }}>
          {v ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Countries',
      dataIndex: 'gatewayAccess',
      width: 130,
      render: (access: GatewayAccess[]) => {
        if (!access?.length) return <Text type="secondary" style={{ fontSize: 11 }}>{'\u2014'}</Text>
        return (
          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
            {access.map((a) => {
              const countries = a.allowedCountries || []
              const isAll = countries.includes('*')
              return (
                <div key={a.operationType}>
                  <Text type="secondary" style={{ fontSize: 10 }}>{a.operationType}: </Text>
                  {isAll ? (
                    <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>All countries</Tag>
                  ) : countries.length > 0 ? (
                    <Flex gap={2} wrap="wrap" style={{ display: 'inline-flex' }}>
                      {countries.slice(0, 4).map((c) => (
                        <Tag
                          key={c}
                          style={{ fontSize: 9, margin: 0, padding: '0 4px' }}
                        >
                          {countryNameByCode[c] || c}
                        </Tag>
                      ))}
                      {countries.length > 4 && (
                        <Tooltip title={countries.slice(4).map((c) => countryNameByCode[c] || c).join(', ')}>
                          <Tag style={{ fontSize: 9, margin: 0, padding: '0 4px', cursor: 'pointer' }}>+{countries.length - 4}</Tag>
                        </Tooltip>
                      )}
                    </Flex>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 10 }}>{'\u2014'}</Text>
                  )}
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      title: 'Limits',
      dataIndex: 'gatewayOperations',
      key: 'limits',
      width: 140,
      render: (ops: GatewayOperation[]) => {
        if (!ops?.length) return <Text type="secondary" style={{ fontSize: 11 }}>{'\u2014'}</Text>
        return (
          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
            {ops.map((op) => (
              <div key={`lim-${op.operationType}`}>
                <Text type="secondary" style={{ fontSize: 10 }}>{op.operationType}: </Text>
                <Text style={{ fontFamily: 'monospace', fontSize: 10 }}>
                  {op.limits || '\u2014'}
                </Text>
              </div>
            ))}
          </div>
        )
      },
    },
    {
      title: 'Weight',
      dataIndex: 'gatewayWeight',
      width: 100,
      render: (weights: GatewayWeight[]) => {
        if (!weights?.length) return <Text type="secondary" style={{ fontSize: 11 }}>{'\u2014'}</Text>
        const allWeights = weights.filter((w) => w.countryCodeA3 === '*')
        return (
          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
            {allWeights.map((w) => (
              <div key={`w-${w.operationType}`}>
                <Text type="secondary" style={{ fontSize: 10 }}>{w.operationType}: </Text>
                <Text style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 500 }}>{w.weight}</Text>
              </div>
            ))}
            {weights.length > allWeights.length && (
              <Tooltip title={weights.filter((w) => w.countryCodeA3 !== '*').map((w) => `${w.operationType} ${w.countryCodeA3}: ${w.weight}`).join(', ')}>
                <Text type="secondary" style={{ fontSize: 10, cursor: 'pointer' }}>
                  +{weights.length - allWeights.length} country
                </Text>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: 'Commission',
      dataIndex: 'gatewayCommissions',
      width: 130,
      render: (comms: GatewayCommission[]) => {
        if (!comms?.length) return <Text type="secondary" style={{ fontSize: 11 }}>{'\u2014'}</Text>
        return (
          <div style={{ fontSize: 11, lineHeight: 1.6 }}>
            {comms.map((c, i) => {
              const parts: string[] = []
              if (c.percentAmount) parts.push(`${c.percentAmount}%`)
              if (c.flatAmount) parts.push(`${c.flatAmount} ${c.flatCurrency}`)
              return (
                <div key={i}>
                  <Text type="secondary" style={{ fontSize: 10 }}>{c.operationType}: </Text>
                  <Text style={{ fontFamily: 'monospace', fontSize: 10 }}>
                    {parts.length ? parts.join(' + ') : '\u2014'}
                  </Text>
                  {(c.minAmount > 0 || c.maxAmount > 0) && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 9 }}>
                        {c.minAmount > 0 ? `min ${c.minAmount} ${c.minCurrency}` : ''}
                        {c.minAmount > 0 && c.maxAmount > 0 ? ' / ' : ''}
                        {c.maxAmount > 0 ? `max ${c.maxAmount} ${c.maxCurrency}` : ''}
                      </Text>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      title: 'Payment Group',
      dataIndex: 'withdrawalGroup',
      width: 140,
      render: (wg: WithdrawalGroup) =>
        wg?.name ? (
          <Text style={{ fontSize: 12 }}>{wg.name}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 11 }}>{'\u2014'}</Text>
        ),
    },
    {
      title: 'Archived',
      dataIndex: 'isDisable',
      width: 80,
      align: 'center',
      render: (v: boolean) => (
        <Tag color={v ? 'error' : 'default'} style={{ fontSize: 10, margin: 0 }}>
          {v ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: '',
      fixed: 'right',
      width: 50,
      render: (_: any, item: Gateway) => (
        <Guard permission="edit_payments">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              href={path.billing.gateway.edit(item.gatewayId)}
            />
          </Tooltip>
        </Guard>
      ),
    },
  ]

  // ── CSV ─────────────────────────────────────────────────
  const csvContent = gateways
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  return (
    <div>
      {/* ── Filters panel ─────────────────────────────────── */}
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        {/* Row 1: search */}
        <Flex gap={6} wrap="wrap" style={{ marginBottom: 10 }}>
          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="Gateway ID"
            value={inputId}
            onChange={(e) => setInputId(e.target.value.replace(/\D/g, ''))}
            onPressEnter={() => applyId(inputId)}
            onBlur={() => applyId(inputId)}
            allowClear
            onClear={() => { setInputId(''); setFilters({ gatewayId: undefined, page: '1' }) }}
            style={{ width: 130, fontFamily: 'monospace' }}
          />
          <Input
            prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
            placeholder="Internal name"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onPressEnter={() => applyName(inputName)}
            onBlur={() => applyName(inputName)}
            allowClear
            onClear={() => { setInputName(''); setFilters({ internalName: undefined, page: '1' }) }}
            style={{ width: 220 }}
          />
          <Select
            mode="multiple"
            placeholder="Activity filter"
            value={activityFilter}
            onChange={(v) => { setActivityFilter(v); setFilter('page', '1') }}
            options={ACTIVITY_OPTIONS}
            style={{ minWidth: 200 }}
            maxTagCount="responsive"
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Payment Group"
            value={paymentGroupFilter}
            onChange={(v) => { setPaymentGroupFilter(v); setFilter('page', '1') }}
            options={paymentGroupOptions}
            style={{ minWidth: 160 }}
            maxTagCount="responsive"
            allowClear
            showSearch
          />
          <Select
            mode="multiple"
            placeholder="Country"
            value={countryFilter}
            onChange={(v) => { setCountryFilter(v); setFilter('page', '1') }}
            options={countryOptions}
            style={{ minWidth: 160 }}
            maxTagCount="responsive"
            allowClear
            showSearch
          />
        </Flex>

        {/* Divider */}
        <div style={{ height: 1, background: 'var(--crm-border, #e2e5ec)', margin: '0 0 10px' }} />

        {/* Row 2: boolean filters */}
        <Flex gap={10} wrap="wrap" align="center">
          <BoolFilter label="Archived" value={filters.archived} onChange={(v) => { setFilters({ archived: v, page: '1' }) }} />
          <BoolFilter label="Verify required" value={verifyRequired} onChange={(v) => { setVerifyRequired(v); setFilter('page', '1') }} />

          {hasActiveFilters && (
            <>
              <div style={{ width: 1, height: 28, background: 'var(--crm-border, #e2e5ec)' }} />
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={resetAll}
                style={{ color: '#ef4444', fontSize: 12 }}
              >
                Reset all
              </Button>
            </>
          )}
        </Flex>
      </div>

      {/* ── Toolbar ───────────────────────────────────────── */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
        <Flex gap={8}>
          <Guard permission="update_payments">
            <Button
              icon={<SyncOutlined />}
              onClick={() => syncPaymentsMethod()}
            >
              Sync payment methods
            </Button>
          </Guard>
          <Guard permission="edit_payments">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => toPath(path.billing.gateway.create())}
            >
              Create new
            </Button>
          </Guard>
        </Flex>
        <Guard permission="view_payments">
          <Button
            icon={<DownloadOutlined />}
            href={csvCode}
            download="gateways.csv"
            target="_blank"
          >
            CSV
          </Button>
        </Guard>
      </Flex>

      {/* ── Table ─────────────────────────────────────────── */}
      <Table
        dataSource={filteredGateways}
        columns={columns}
        scroll={{ x: 1100 }}
        size="small"
        rowKey="gatewayId"
        loading={loading}
        rowClassName={(_record, index) =>
          index % 2 === 1 ? 'ant-table-row-striped' : ''
        }
        pagination={{
          current: page,
          pageSize,
          size: 'default',
          showSizeChanger: true,
          pageSizeOptions: [15, 30, 50, 100],
          total: filters.gatewayId || filters.internalName || activityFilter.length || verifyRequired !== undefined
              ? filteredGateways.length
              : totalCountOfGateways,
          showTotal: (t, [f, to]) => `${f}\u2013${to} of ${t}`,
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, string | undefined> = {}
          if (pagination.current) updates.page = pagination.current === 1 ? undefined : String(pagination.current)
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          if (sorter.field) {
            // @ts-ignore
            const field = sorter.field === 'gatewayId' ? 'id' : sorter.field
            updates.sortField = field === 'id' ? undefined : field
            // @ts-ignore
            updates.sortOrder = sorter.order === 'descend' ? undefined : 'ascend'
          }
          setFilters(updates as any)
        }}
      />
      <style>{`
        .ant-table-row-striped td {
          background: rgba(0, 0, 0, 0.02) !important;
        }
        .ant-table-cell { vertical-align: middle !important; }
      `}</style>
    </div>
  )
}
